import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Input } from 'antd'
import ImageCropper from 'components/Elements/ImageCropper'
import ModalStyledLoader from 'components/Elements/ModalStyledLoader'
import { useAtom } from 'jotai'
import { useState } from 'react'
import { Button, Col, Image, Row } from 'react-bootstrap'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { useTranslation } from 'react-i18next'
import { BiMoveVertical } from 'react-icons/bi'
import { FaTrash } from 'react-icons/fa'
import axiosClient from 'services/axios-client'
import { activeAnswerAtom, activeQuestionAtom } from 'store/atoms'
import { fetchAnswerImages } from 'store/queries'

const { TextArea } = Input

const SortableFile = ({ file, onRemovePendingFile }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: file.uid })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      key={file.uid}
      className="pending-file-item"
    >
      <Image src={file.base64} className="mr-3" width={50} />
      <FaTrash className="mr-2" onClick={() => onRemovePendingFile(file.uid)} />
      <span>{file.name}</span>
    </div>
  )
}

const SortableImage = ({ img, onCaptionUpdate, onDelete }) => {
  const { t } = useTranslation()
  const [caption, setCaption] = useState(img.caption)

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: img.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} key={img.id}>
      <div className="current-img d-flex align-items-center">
        <FaTrash className="icon-trash cursor" onClick={() => onDelete(img)} />
        <div className="d-flex align-items-center w-100">
          <div className="img-wrapper">
            <Image src={img.url} width={96} height="auto" />
          </div>
          <TextArea
            rows="2"
            placeholder={t('click_to_add_caption')}
            value={caption}
            onChange={(e) => {
              setCaption(e.target.value)
            }}
            maxLength={160}
          />
          {caption !== img.caption && (
            <div className="d-flex actions">
              <Button
                className="btn-blue btn btn-primary"
                onClick={() => {
                  onCaptionUpdate(img, caption)
                }}
              >
                {t('save')}
              </Button>
              <Button
                className="btn-white btn btn-primary"
                onClick={() => {
                  setCaption(img.caption)
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          )}

          <BiMoveVertical {...listeners} />
        </div>
      </div>
    </div>
  )
}

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })

export default function Images({ bookId, isMobile = false, onSaveDraft }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [activeAnswer] = useAtom(activeAnswerAtom)
  const [question] = useAtom(activeQuestionAtom)
  const questionId = question?.id
  const answerId = activeAnswer?.id

  const [pendingUploads, setPendingUploads] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  const { data: images } = useQuery({
    queryKey: ['images', { bookId, questionId, answerId }],
    queryFn: () => fetchAnswerImages(bookId, questionId, answerId),
  })

  const onRemove = (file) => {
    setPendingUploads(() => {
      const index = pendingUploads.indexOf(file)
      const newPendingUploads = pendingUploads.slice()
      newPendingUploads.splice(index, 1)
      return newPendingUploads
    })
  }

  const onDeleteImage = async (image) => {
    const { ok } = await axiosClient.delete(
      `/image/${image.id}?book_question_join_id=${activeAnswer?.book_question_join_id}`,
    )
    if (ok) {
      // Book stats are dependent on images.
      queryClient.invalidateQueries({ queryKey: ['book', bookId, 'stats'] })
      queryClient.invalidateQueries({
        queryKey: [
          'images',
          {
            bookId,
            questionId,
            answerId,
          },
        ],
      })
    }
  }

  const onSort = async ({ active, over }) => {
    if (active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id)
      const newIndex = images.findIndex((img) => img.id === over.id)
      const reorderedImgs = arrayMove(images, oldIndex, newIndex)

      reorderedImgs.forEach((img, i) => {
        img.sort = i
      })

      const { ok } = await axiosClient.post(`/images/reorder`, {
        sort_data: reorderedImgs,
        book_question_join_id: activeAnswer?.book_question_join_id,
      })
      if (ok) {
        queryClient.invalidateQueries({ queryKey: ['book', bookId, 'stats'] })
        queryClient.invalidateQueries({
          queryKey: [
            'images',
            {
              bookId,
              questionId,
              answerId,
            },
          ],
        })
      }
    }
  }

  // Also creates answer if not yet saved.
  const onUploadPendingImages = async () => {
    setIsUploading(true)

    let answer = activeAnswer

    if (!answer?.id) {
      const createdAnswer = await onSaveDraft()

      answer = createdAnswer
    }
    const answerId = answer?.id

    const promises = []

    pendingUploads.forEach(async (file) => {
      promises.push(
        axiosClient
          .post('/images/upload', {
            base64: file.base64,
            name: file.name,
            type: file.type,
            book_question_join_id: answer?.book_question_join_id,
          })
          .then((res) => {
            return axiosClient.post(
              `/book/${bookId}/question/${answer?.book_question_join_id}/answer/${answerId}/image`,
              {
                key: res.data.key,
                caption: file.caption || '',
                name: file.name,
                sort: file.sort,
                orientation: file.orientation || 'horizontal',
              },
            )
          }),
      )
    })

    Promise.all(promises).then(async () => {
      setPendingUploads([])
      const imagesResult = await axiosClient.get(
        `/book/${bookId}/question/${questionId}/answer/${answerId}/images`,
      )
      if (imagesResult.ok) {
        // We need to invalidate to update stats in book info.
        queryClient.invalidateQueries({ queryKey: ['book', bookId, 'stats'] })
        queryClient.invalidateQueries({
          queryKey: [
            'images',
            {
              bookId,
              questionId,
              answerId,
            },
          ],
        })
        setIsUploading(false)
      }
    })
  }

  const onSortEnd = async ({ active, over }) => {
    if (active.id !== over.id) {
      const oldIndex = pendingUploads.findIndex((f) => f.uid === active.id)
      const newIndex = pendingUploads.findIndex((f) => f.uid === over.id)
      const reorderedFiles = arrayMove(pendingUploads, oldIndex, newIndex)

      reorderedFiles.forEach((f, i) => {
        f.sort = i
      })
      setPendingUploads(reorderedFiles)
    }
  }

  const onRemovePendingFile = (fileId) => {
    setPendingUploads(pendingUploads.filter((_file) => _file.uid !== fileId))
  }

  const onCaptionUpdate = async (img, newCaption) => {
    if (img.caption !== newCaption) {
      const { ok } = await axiosClient.put(
        `/book/${bookId}/question/${questionId}/answer/${answerId}/image/${img.id}`,
        {
          ...img,
          caption: newCaption,
          book_question_join_id: activeAnswer?.book_question_join_id,
        },
      )

      if (ok) {
        queryClient.invalidateQueries({ queryKey: ['book', bookId, 'stats'] })
        queryClient.invalidateQueries({
          queryKey: [
            'images',
            {
              bookId,
              questionId,
              answerId,
            },
          ],
        })
      }
    }
  }

  const handleAddPendingUpload = async (file) => {
    let newFiles = [...pendingUploads]

    if (!file.base64) {
      file.base64 = await toBase64(file)
    }

    file.sort = pendingUploads.length

    newFiles.push(file)

    setPendingUploads(newFiles)
  }

  // Required to allow mouse events on draggable
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
  )

  return (
    <>
      <div className="answer-content-wrapper modal-custom-body">
        <div className="answer-images">
          <Row>
            <Col
              md={{ span: 6 }}
              className="p20204520"
              style={{ borderRight: '5px solid #f4f3f3' }}
            >
              <ImageCropper
                rotate
                aspect={11 / 7}
                onRemove={onRemove}
                onAddPendingUpload={handleAddPendingUpload}
                pendingUploads={pendingUploads}
              />
            </Col>
            <Col
              md={{ span: 6 }}
              className="mobile-pending-images-body-border p20402020"
              style={{ borderLeft: '5px solid #f4f3f3' }}
            >
              <div className="pending-images pending-images-body">
                <h4 className={`${isMobile ? 'mt-20' : ''}`}>
                  {t('pending_pictures')}
                </h4>
                <div className="pending-list">
                  {pendingUploads.length > 0 ? (
                    <DndContext
                      onDragEnd={(f) => onSortEnd(f)}
                      sensors={sensors}
                    >
                      <SortableContext items={pendingUploads.map((f) => f.uid)}>
                        {pendingUploads.map((f) => (
                          <SortableFile
                            key={f.uid}
                            file={f}
                            onRemovePendingFile={onRemovePendingFile}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <p style={{ borderTop: '3px solid #f2f2f2' }}>
                      No pending uploads
                    </p>
                  )}
                </div>
                <div className="btn-upload-wrapper actions text-right">
                  <Button
                    className="btn-blue btn-dash"
                    onClick={onUploadPendingImages}
                  >
                    {isUploading ? (
                      <ModalStyledLoader isglobal={false} small />
                    ) : (
                      t('upload')
                    )}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="answer-content-wrapper modal-custom-body picture p20 mt-20">
        <div className="answer-images">
          <div className="current-images pending-images-body">
            <h4>{t('current_pictures')}</h4>
            <div className="current-list">
              {images?.length ? (
                <DndContext onDragEnd={(img) => onSort(img)}>
                  <SortableContext items={images.map((img) => img.id)}>
                    {images.map((img) => (
                      <SortableImage
                        key={img.id}
                        img={img}
                        onCaptionUpdate={onCaptionUpdate}
                        onDelete={onDeleteImage}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                <p style={{ borderTop: '3px solid #f2f2f2' }}>No files</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
